import {
  Flex,
  Text,
  RadioButtonCheckedIcon,
  RadioButtonUncheckedIcon,
  type SxPropsWithTheme,
  mergeSx,
} from '@shared/components';
import { randomUUID } from '@shared/utils';

interface Option {
  label: React.ReactNode;
  icon?: React.ReactNode;
  checked?: boolean;
  sx?: SxPropsWithTheme;
}

export interface ReadonlyRadioGroupProps {
  options: Option[];
  label?: string;
  row?: boolean;
  helperText?: string;
  required?: boolean;
  sx?: SxPropsWithTheme;
}

export default function ReadonlyRadioGroup(props: Readonly<ReadonlyRadioGroupProps>) {
  const { label, options, row: hasRow, helperText, required, sx } = props;

  return (
    <Flex sx={(theme) => ({ gap: theme.spacing(2), flexDirection: 'column' })}>
      {label ? (
        <Text variant='body2Semibold'>
          {label}
          {required ? (
            <Text
              component='span'
              color='error.main'
              sx={{
                ml: (theme) => theme.spacing(1),
              }}
            >
              *
            </Text>
          ) : null}
        </Text>
      ) : null}
      <Flex
        sx={mergeSx(
          {
            gap: (theme) => theme.spacing(4),
            flexDirection: hasRow ? 'row' : 'column',
          },
          sx,
        )}
      >
        {options.map((option) => (
          <Flex
            key={randomUUID()}
            sx={mergeSx(
              (theme) => ({
                flexDirection: 'row',
                color: theme.palette.grey[600],
                borderRadius: theme.shape.rounded(1.5),
                alignItems: 'center',
              }),
              option.sx,
            )}
          >
            {option.checked ? (
              <RadioButtonCheckedIcon
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                  mr: theme.spacing(2),
                })}
              />
            ) : (
              <RadioButtonUncheckedIcon
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                  mr: theme.spacing(2),
                })}
              />
            )}
            <Flex>
              {option.icon}
              {typeof option.label === 'string' ? (
                <Text
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    flex: 1,
                  })}
                >
                  {option.label}
                </Text>
              ) : (
                option.label
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
      {helperText ? <Text variant='caption'>{helperText}</Text> : null}
    </Flex>
  );
}
